import { Link } from "react-router-dom";
import { ArrowRight, BellRing, Check, LayoutDashboard, QrCode, Smartphone, Star } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Logo } from "../components/Logo";

const HERO_IMG = "https://images.unsplash.com/photo-1615854432109-3ee55bd07c7d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const Treeline = ({ className = "" }) => (
  <svg viewBox="0 0 1060 90" preserveAspectRatio="none" className={className} aria-hidden="true" fill="currentColor">
    <path d="M0 90V70l30-40 18 26 22-48 26 46 16-22 24 38 30-60 28 50 18-24 26 34 34-58 30 54 20-28 22 30 28-52 32 56 18-20 26 32 34-62 30 58 22-30 26 34 30-50 28 46 20-26 24 36 32-60 30 56 18-24 28 38 30-54 26 48 22-28 24 34 30-58 32 60 20-30 26 36 28-48 22 32 30-40V90z" />
  </svg>
);

const STEPS = [
  { n: "01", title: "Sign up in minutes", body: "Create your business account and subscribe online. No calls, no setup fees." },
  { n: "02", title: "Share your QR code", body: "Print it on receipts, tables or the counter. Every scan lands on your own survey." },
  { n: "03", title: "Listen & respond", body: "Watch ratings roll in live and get alerted the moment something goes wrong." },
];

const FEATURES = [
  { icon: Star, title: "Star ratings that matter", body: "Overall satisfaction plus quick scores for the categories you choose — Service, Quality, Cleanliness or your own." },
  { icon: BellRing, title: "Low-rating alerts", body: "Anything under 3 stars is flagged in red and emailed to you instantly." },
  { icon: LayoutDashboard, title: "Clean dashboard", body: "Averages, category breakdowns and feedback sorted by score, at a glance." },
  { icon: Smartphone, title: "Built for phones", body: "A 30-second survey your customers can finish before their coffee cools." },
];

const HeroMock = () => (
  <div className="absolute -bottom-8 -left-4 sm:-left-10 w-60 sm:w-64 rounded-3xl bg-white p-5 shadow-2xl border border-slate-200 fade-up" style={{ animationDelay: "300ms" }}>
    <p className="label-caps">How was your visit?</p>
    <div className="mt-3 flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-7 h-7 ${n <= 4 ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}
    </div>
    <div className="mt-4 space-y-2 text-xs text-[#4B5A54]">
      {["Service", "Quality", "Cleanliness"].map((c, i) => (
        <div key={c} className="flex justify-between"><span>{c}</span><span className="font-semibold text-[#1A2B25]">{[5, 4, 5][i]}.0</span></div>
      ))}
    </div>
  </div>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#EEF1ED] text-[#1A2B25]">
      <Navbar />
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-24 grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
        <div className="fade-up">
          <span className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 py-1 text-xs font-semibold text-[#4B5A54]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2E6F8E]" /> Made in the Pacific Northwest
          </span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Hear every customer.<br /><span className="text-[#2F5D4A]">Fix things fast.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#4B5A54] max-w-lg leading-relaxed">
            From Seattle coffee counters to Portland bike shops, Bonus Thought gives your business a personal QR survey. Customers rate you in seconds, and you see every score — with instant alerts when someone's unhappy.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/register" className="btn-primary !px-6 !py-3.5 text-base" data-testid="hero-signup-cta">Start your free month <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/s/sunny-side-cafe" className="btn-secondary !px-6 !py-3.5 text-base" data-testid="hero-demo-survey-link"><QrCode className="w-4 h-4" /> Try a live survey</Link>
          </div>
        </div>
        <div className="relative fade-up" style={{ animationDelay: "150ms" }}>
          <img src={HERO_IMG} alt="Barista behind a wooden coffee bar" className="w-full h-[380px] sm:h-[460px] object-cover rounded-[2rem] shadow-xl" />
          <HeroMock />
        </div>
      </section>

      <section id="how" className="bg-white border-y border-slate-200/70">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
          <p className="label-caps">How it works</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-md">Up and running before lunch.</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-10">
            {STEPS.map((s) => (
              <div key={s.n} className="border-t-2 border-[#1A2B25] pt-5">
                <span className="font-mono text-sm text-[#2F5D4A] font-semibold">{s.n}</span>
                <h3 className="mt-2 font-display text-xl font-bold">{s.title}</h3>
                <p className="mt-2 text-[#4B5A54] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-24 grid lg:grid-cols-[0.8fr_1.2fr] gap-12">
        <div>
          <p className="label-caps">Features</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Everything you need. Nothing you don't.</h2>
          <p className="mt-4 text-[#4B5A54] leading-relaxed">No bloated analytics suite. Just the ratings, comments and alerts that help you run a better place.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-2xl bg-white border border-slate-200/80 p-6 card-rise">
              <span className="grid place-items-center w-10 h-10 rounded-xl bg-[#E3EDE6] text-[#2F5D4A]"><Icon className="w-5 h-5" /></span>
              <h3 className="mt-4 font-display text-lg font-bold">{title}</h3>
              <p className="mt-1.5 text-sm text-[#4B5A54] leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className="max-w-6xl mx-auto px-5 sm:px-8 pb-24">
        <div className="relative overflow-hidden rounded-[2rem] bg-[#1A2B25] text-white p-8 sm:p-14 pb-20 sm:pb-24 grid md:grid-cols-2 gap-10 items-center">
          <Treeline className="absolute bottom-0 left-0 w-full h-16 text-[#2F5D4A]/60" />
          <div>
            <p className="text-xs font-semibold tracking-wider uppercase text-white/60">Simple pricing</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">One plan. Every feature.</h2>
            <div className="mt-6 flex items-baseline gap-1">
              <span className="font-display text-6xl font-extrabold" data-testid="pricing-amount">$29.99</span><span className="text-white/60">/ month per location</span>
            </div>
            <div className="mt-4 inline-flex rounded-full bg-[#D9A441] text-[#1A2B25] px-3 py-1 text-xs font-bold uppercase tracking-wide" data-testid="pricing-trial-badge">
              First month free for new customers
            </div>
          </div>
          <div>
            <ul className="space-y-3">
              {["Personal survey link + QR code for every location", "All locations under one login", "Unlimited responses", "Email alerts under 3 stars", "Cancel anytime"].map((p) => (
                <li key={p} className="flex items-center gap-3"><Check className="w-5 h-5 text-[#D9A441]" />{p}</li>
              ))}
            </ul>
            <Link to="/register" className="btn-primary mt-8 w-full justify-center !py-3.5 text-base" data-testid="pricing-signup-cta">Create your account</Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200/70">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-[#778580]">
          <Logo />
          <p>© {new Date().getFullYear()} Bonus Thought. Rooted in the Pacific Northwest.</p>
        </div>
      </footer>
    </div>
  );
}
